import React from 'react';
import { FileText, Calendar, CheckCircle, FileArchive, MessageSquare, Video, Download, Plus, TrendingUp, Users, Shield, Activity, BarChart2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '../stores/uiStore';
import { DashboardStats } from '../components/dashboard/DashboardStats';
import { TeamPerformance } from '../components/dashboard/TeamPerformance';
import { BidTimeline } from '../components/dashboard/BidTimeline';
import { ComplianceOverview } from '../components/dashboard/ComplianceOverview';
import { DeadlineNotifications } from '../components/dashboard/DeadlineNotifications';
import { ExportReport } from '../components/export/ExportReport';
import { NewProposalModal } from '../components/proposals/NewProposalModal';
import { PerformanceInsights } from '../components/dashboard/PerformanceInsights';
import { CollaborationPanel } from '../components/dashboard/CollaborationPanel';
import { RecentActivities } from '../components/dashboard/RecentActivities';
import { ResourceUtilization } from '../components/dashboard/ResourceUtilization';
import { ContractAnalytics } from '../components/dashboard/ContractAnalytics';
import { ComplianceAlerts } from '../components/dashboard/ComplianceAlerts';
import { UpcomingMeetings } from '../components/dashboard/UpcomingMeetings';
import { QuickActions } from '../components/dashboard/QuickActions';

export function Dashboard() {
  const navigate = useNavigate();
  const { openModal } = useUIStore();

  const handleExportReport = () => {
    openModal('exportReport');
  };

  const handleNewProposal = () => {
    openModal('newProposal');
  };

  return (
    <div className="min-h-screen space-y-6 animate-fade-in-up">
      {/* Header with Gradient Background */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-8 shadow-lg">
        <div className="absolute inset-0 bg-grid-white/[0.1] bg-[size:16px_16px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        
        <div className="relative">
          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white sm:text-3xl">Dashboard</h1>
              <p className="mt-1 text-sm text-gray-100">
                Overview of your contracting activities and performance metrics
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleExportReport}
                className="inline-flex items-center rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm transition-all hover:bg-white/20"
              >
                <Download className="mr-2 h-4 w-4" />
                Export Report
              </button>
              <button
                onClick={handleNewProposal}
                className="inline-flex items-center rounded-lg bg-white px-4 py-2 text-sm font-medium text-purple-600 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-gray-50 hover:shadow-md"
              >
                <Plus className="mr-2 h-4 w-4" />
                New Proposal
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="animate-fade-in-up [animation-delay:100ms]">
        <QuickActions />
      </div>

      {/* Stats Grid */}
      <div className="animate-fade-in-up [animation-delay:200ms]">
        <DashboardStats />
      </div>

      {/* Performance Insights */}
      <div className="animate-fade-in-up [animation-delay:300ms]">
        <PerformanceInsights />
      </div>

      {/* Contract Analytics */}
      <div className="animate-fade-in-up [animation-delay:400ms]">
        <ContractAnalytics />
      </div>

      {/* Three Column Layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Team Performance */}
        <div className="animate-fade-in-up [animation-delay:500ms]">
          <div className="h-full rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
            <TeamPerformance />
          </div>
        </div>

        {/* Bid Timeline */}
        <div className="animate-fade-in-up [animation-delay:500ms]">
          <div className="h-full rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
            <BidTimeline />
          </div>
        </div>

        {/* Resource Utilization */}
        <div className="animate-fade-in-up [animation-delay:500ms]">
          <div className="h-full rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
            <ResourceUtilization />
          </div>
        </div>
      </div>

      {/* Collaboration and Activities */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Collaboration Panel */}
        <div className="animate-fade-in-up [animation-delay:600ms]">
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
            <CollaborationPanel />
          </div>
        </div>

        {/* Recent Activities */}
        <div className="animate-fade-in-up [animation-delay:600ms]">
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
            <RecentActivities />
          </div>
        </div>
      </div>

      {/* Upcoming Meetings */}
      <div className="animate-fade-in-up [animation-delay:700ms]">
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
          <UpcomingMeetings />
        </div>
      </div>

      {/* Deadlines and Compliance */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Deadline Notifications */}
        <div className="animate-fade-in-up [animation-delay:800ms]">
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
            <DeadlineNotifications />
          </div>
        </div>

        {/* Compliance Overview */}
        <div className="animate-fade-in-up [animation-delay:800ms]">
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
            <ComplianceOverview />
          </div>
        </div>
      </div>

      {/* Compliance Alerts */}
      <div className="animate-fade-in-up [animation-delay:900ms]">
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
          <ComplianceAlerts />
        </div>
      </div>

      {/* Modals */}
      <ExportReport />
      <NewProposalModal />

      {/* Decorative Elements */}
      <div className="pointer-events-none fixed inset-0 z-30 overflow-hidden">
        <div className="absolute -left-1/2 top-0 h-[1000px] w-[1000px] rounded-full bg-purple-500/5 blur-3xl"></div>
        <div className="absolute -right-1/2 bottom-0 h-[1000px] w-[1000px] rounded-full bg-purple-500/5 blur-3xl"></div>
      </div>
    </div>
  );
}